import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, ImageUp, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { isSpecies, SPECIES, type Species } from '@zt/content';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
  type Tone,
  buttonStyles,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { ProductDescriptionDraft } from './ai/drafts';
import type {
  AttributeDef,
  Brand,
  Category,
  Country,
  PriceList,
  ProductDetail,
  ProductPage,
  ProductPrices,
  ProductTag,
  Store,
  TaxClass,
} from '../lib/types';
import { useAttributeDefs } from './attributes';
import { slugify } from './brands';
import { BulkBar, useBulkKinds, type BulkFilter } from './product-bulk';
import { GoogleAdsBulk, ProductGoogleAds, useGoogleAdsFilterOptions } from './google-ads';
import { PriceTiers } from './price-tiers';
import { extrasBody, extrasFromDetail, ProductExtrasSection } from './product-extras';
import { ProductToolsActions, SkuSuggestButton, TemplatePicker } from './product-tools';
import { ProductComplianceSection } from '../components/ProductCompliance';
import { ProductTranslationsSection } from '../components/Translations';

const fileUrl = (id: string) => `/v1/files/${id}/content`;

export const statusTone: Record<string, Tone> = { draft: 'slate', active: 'moss', archived: 'neutral' };
const vetTone: Record<string, Tone> = {
  pending: 'amber',
  approved: 'moss',
  rejected: 'rose',
  not_required: 'neutral',
};

export function useCatalogRefs() {
  const brands = useQuery({ queryKey: ['brands'], queryFn: () => api.get<Brand[]>('/v1/admin/brands') });
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
  });
  const taxes = useQuery({
    queryKey: ['tax-classes'],
    queryFn: () => api.get<TaxClass[]>('/v1/admin/tax-classes'),
  });
  return { brands: brands.data ?? [], categories: categories.data ?? [], taxes: taxes.data ?? [] };
}

// ---------------------------------------------------------------------------
// Ürün listesi
// ---------------------------------------------------------------------------

export function ProductsPage() {
  const t = useT();
  const { can } = usePermissions();
  const { brands } = useCatalogRefs();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [vet, setVet] = useState('');
  const [brandId, setBrandId] = useState('');
  const [tag, setTag] = useState('');
  // Google reklamları süzgeci (F7-38): bir kampanyada / kampanyada değil / Google'a göre uygun değil / kampanya.
  const canAds = can('marketing.ads.read');
  const [ads, setAds] = useState('');
  const adsCampaigns = useGoogleAdsFilterOptions(canAds);
  const tags = useQuery({
    queryKey: ['product-tags'],
    queryFn: () => api.get<ProductTag[]>('/v1/admin/product-tags'),
  });
  const [cursor, setCursor] = useState<string | null>(null);
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  if (vet) params.set('vetReview', vet);
  if (brandId) params.set('brandId', brandId);
  if (tag) params.set('tag', tag);
  if (ads) params.set('ads', ads);
  if (cursor) params.set('cursor', cursor);
  const list = useQuery({
    queryKey: ['products', params.toString()],
    queryFn: () => api.get<ProductPage>(`/v1/admin/products?${params.toString()}`),
  });
  // Toplu işlem seçimi (F5B-08): süzgeç değişince seçim temizlenir.
  const canBulk = useBulkKinds().length > 0;
  const [selected, setSelected] = useState<string[]>([]);
  const [allMatching, setAllMatching] = useState(false);
  const clearSelection = () => {
    setSelected([]);
    setAllMatching(false);
  };
  useEffect(() => {
    setCursor(null);
    setSelected([]);
    setAllMatching(false);
  }, [q, status, vet, brandId, tag, ads]);
  const pageIds = list.data?.items.map((p) => p.id) ?? [];
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const filter: BulkFilter = {
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(status ? { status } : {}),
    ...(vet ? { vetReview: vet } : {}),
    ...(brandId ? { brandId } : {}),
    ...(tag ? { tag } : {}),
  };
  const toggle = (id: string, on: boolean) => {
    const base = allMatching ? [...new Set([...selected, ...pageIds])] : selected;
    setAllMatching(false);
    setSelected(on ? [...new Set([...base, id])] : base.filter((x) => x !== id));
  };
  return (
    <PageFrame
      helpKey="page.products"
      actions={
        <>
          <div className="flex items-center gap-1">
            <Link to="/urun-tablosu" className={buttonStyles({ variant: 'secondary' })}>
              {t('admin.productGrid.open')}
            </Link>
            <HelpTip k="products.grid" />
          </div>
          <div className="flex items-center gap-1">
            <Link to="/toplu-islemler" className={buttonStyles({ variant: 'secondary' })}>
              {t('admin.productBulk.history')}
            </Link>
            <HelpTip k="products.bulk_history" />
          </div>
          {can('catalog.product.write') && (
            <div className="flex items-center gap-1">
              <Link
                to="/urunler/$id"
                params={{ id: 'yeni' }}
                className={buttonStyles({ variant: 'primary' })}
              >
                <Plus className="h-4 w-4" aria-hidden />
                {t('admin.products.create')}
              </Link>
              <HelpTip k="products.create" />
            </div>
          )}
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label={t('admin.products.search')} className="sm:col-span-4">
          {(p) => (
            <Input
              {...p}
              type="search"
              value={q}
              placeholder={t('admin.products.searchPlaceholder')}
              onChange={(e) => setQ(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('admin.products.status')}>
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {['draft', 'active', 'archived'].map((s) => (
                <option key={s} value={s}>
                  {t(`admin.products.statuses.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.products.vetReview')}>
          {(p) => (
            <Select {...p} value={vet} onChange={(e) => setVet(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {['pending', 'approved', 'rejected', 'not_required'].map((s) => (
                <option key={s} value={s}>
                  {t(`admin.products.vetStatuses.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.products.brand')}>
          {(p) => (
            <Select {...p} value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.products.tag')}>
          {(p) => (
            <Select {...p} value={tag} onChange={(e) => setTag(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {(tags.data ?? []).map((x) => (
                <option key={x.tag} value={x.tag}>
                  {x.tag}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {canAds && (
          <Field label={t('admin.gads.filterLabel')} help={<HelpTip k="gads.filter" />}>
            {(p) => (
              <Select {...p} value={ads} onChange={(e) => setAds(e.target.value)}>
                <option value="">{t('common.all')}</option>
                <option value="any">{t('admin.gads.filterAny')}</option>
                <option value="none">{t('admin.gads.filterNone')}</option>
                <option value="not_eligible">{t('admin.gads.filterNotEligible')}</option>
                {adsCampaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </div>
      {can('marketing.ads.manage') && selected.length > 0 && !allMatching && (
        <GoogleAdsBulk productIds={selected} onDone={clearSelection} />
      )}
      {canBulk && (selected.length > 0 || allMatching) && (
        <BulkBar
          count={selected.length}
          allMatching={allMatching}
          canSelectAllMatching={allOnPage && !!list.data?.nextCursor}
          onSelectAllMatching={() => setAllMatching(true)}
          onClear={clearSelection}
          selection={allMatching ? { filter } : { productIds: selected }}
        />
      )}
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data?.items.length ? (
        <EmptyState title={t('admin.products.empty')} description={t('admin.products.emptyLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[44rem] text-sm">
            <thead className="bg-surface-2 text-start text-ink-2">
              <tr>
                {canBulk && (
                  <th className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      className="accent-[var(--zt-pine)]"
                      aria-label={t('admin.productBulk.selectAllPage')}
                      checked={allOnPage || allMatching}
                      onChange={(e) => {
                        setAllMatching(false);
                        setSelected((cur) =>
                          e.target.checked
                            ? [...new Set([...cur, ...pageIds])]
                            : cur.filter((x) => !pageIds.includes(x)),
                        );
                      }}
                    />
                  </th>
                )}
                <th className="px-3 py-2 text-start font-medium">{t('admin.products.product')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.products.sku')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.products.status')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.products.vetReview')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.products.stock')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((p) => (
                <tr key={p.id} className="border-t border-line">
                  {canBulk && (
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        className="accent-[var(--zt-pine)]"
                        aria-label={t('admin.productBulk.selectRow', { title: p.title })}
                        checked={allMatching || selected.includes(p.id)}
                        onChange={(e) => toggle(p.id, e.target.checked)}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-sm border border-line bg-surface-2">
                        {p.imageFileId && (
                          <img src={fileUrl(p.imageFileId)} alt="" className="h-full w-full object-cover" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <Link
                          to="/urunler/$id"
                          params={{ id: p.id }}
                          className="font-medium text-pine underline-offset-2 hover:underline"
                        >
                          {p.title}
                        </Link>
                        <p className="text-ink-3">
                          {p.brandName} · {t('admin.products.variantCount', { count: p.variantCount })}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 tabular">{p.firstSku ?? '—'}</td>
                  <td className="px-3 py-2">
                    <Badge tone={statusTone[p.status]}>{t(`admin.products.statuses.${p.status}`)}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={vetTone[p.vetReviewStatus]}>
                      {t(`admin.products.vetStatuses.${p.vetReviewStatus}`)}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-end tabular">{p.onHand}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {list.data?.nextCursor && (
        <Button onClick={() => setCursor(list.data!.nextCursor)}>{t('common.more')}</Button>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------
// Ürün düzenleme
// ---------------------------------------------------------------------------

export interface VariantForm {
  id?: string;
  sku: string;
  barcode: string;
  weightGrams: string;
  cost: string;
  optionValues: Record<string, string>;
  status: 'active' | 'archived';
}

export interface ProductForm {
  title: string;
  slug: string;
  slugTouched: boolean;
  brandId: string;
  species: Species[];
  shortDescription: string;
  description: string;
  composition: string;
  usage: string;
  warnings: string;
  feedRegistrationNo: string;
  taxClassId: string;
  /** Gümrük (F7-13): GTİP/HS kodu, menşe ülke, gümrük beyanındaki tanım. */
  hsCode: string;
  originCountry: string;
  customsDescription: string;
  b2cVisible: boolean;
  b2bVisible: boolean;
  /** Bayi adet kuralı (F7-05). */
  dealerMinQuantity: string;
  dealerPackSize: string;
  healthContent: boolean;
  subscribable: boolean;
  seoTitle: string;
  seoDescription: string;
  /** Google alışveriş (F5-11). */
  merchantInclude: boolean;
  googleCategory: string;
  condition: 'new' | 'refurbished' | 'used';
  identifierExists: 'auto' | 'no';
  skuIsMpn: boolean;
  customLabels: string[];
  categoryIds: string[];
  /** Virgülle ayrılmış etiketler (F5B-01). */
  tags: string;
  /** Özellik değerleri: kimlik → değer listesi (çoklu seçenek dışında en fazla bir öğe). */
  attributes: Record<string, string[]>;
  options: Array<{ name: string; values: string }>;
  variants: VariantForm[];
  status: 'draft' | 'active' | 'archived';
}

function emptyForm(): ProductForm {
  return {
    title: '',
    slug: '',
    slugTouched: false,
    brandId: '',
    species: [],
    shortDescription: '',
    description: '',
    composition: '',
    usage: '',
    warnings: '',
    feedRegistrationNo: '',
    taxClassId: '',
    hsCode: '',
    originCountry: '',
    customsDescription: '',
    b2cVisible: true,
    b2bVisible: true,
    dealerMinQuantity: '',
    dealerPackSize: '',
    healthContent: true,
    subscribable: false,
    seoTitle: '',
    seoDescription: '',
    merchantInclude: true,
    googleCategory: '',
    condition: 'new',
    identifierExists: 'auto',
    skuIsMpn: false,
    customLabels: ['', '', '', '', ''],
    categoryIds: [],
    tags: '',
    attributes: {},
    options: [],
    variants: [{ sku: '', barcode: '', weightGrams: '', cost: '', optionValues: {}, status: 'active' }],
    status: 'draft',
  };
}

function fromDetail(p: ProductDetail): ProductForm {
  return {
    title: p.title,
    slug: p.slug,
    slugTouched: true,
    brandId: p.brandId,
    species: (p.species ?? []).filter(isSpecies),
    shortDescription: p.shortDescription ?? '',
    description: p.description ?? '',
    composition: p.composition ?? '',
    usage: p.usage ?? '',
    warnings: p.warnings ?? '',
    feedRegistrationNo: p.feedRegistrationNo ?? '',
    taxClassId: p.taxClassId ?? '',
    hsCode: p.hsCode ?? '',
    originCountry: p.originCountry ?? '',
    customsDescription: p.customsDescription ?? '',
    b2cVisible: p.b2cVisible,
    b2bVisible: p.b2bVisible,
    dealerMinQuantity: p.dealerMinQuantity ? String(p.dealerMinQuantity) : '',
    dealerPackSize: p.dealerPackSize ? String(p.dealerPackSize) : '',
    healthContent: p.healthContent,
    subscribable: p.subscribable,
    seoTitle: p.seoTitle ?? '',
    seoDescription: p.seoDescription ?? '',
    merchantInclude: !p.merchant?.exclude,
    googleCategory: p.merchant?.googleCategory ?? '',
    condition: p.merchant?.condition ?? 'new',
    identifierExists: p.merchant?.identifierExists ?? 'auto',
    skuIsMpn: p.merchant?.skuIsMpn ?? false,
    customLabels: [0, 1, 2, 3, 4].map((i) => p.merchant?.customLabels?.[i] ?? ''),
    categoryIds: p.categoryIds,
    tags: (p.tags ?? []).join(', '),
    attributes: Object.fromEntries(p.attributes.map((a) => [a.attributeId, a.values])),
    options: p.options.map((o) => ({ name: o.name, values: (o.values ?? []).join(', ') })),
    variants: p.variants.map((v) => ({
      id: v.id,
      sku: v.sku,
      barcode: v.barcode ?? '',
      weightGrams: v.weightGrams === null ? '' : String(v.weightGrams),
      cost: minorToInput(v.costAmountMinor),
      optionValues: v.optionValues,
      status: v.status,
    })),
    status: p.status,
  };
}

const optional = (s: string) => (s.trim() ? s.trim() : null);

/** Gönderilecek özellik değerleri (boşlar gönderilmez = kaldırılır); hata alanı sıradaki konuma göredir. */
export function attributeBody(f: Pick<ProductForm, 'attributes'>, defs: AttributeDef[]) {
  return defs
    .map((d) => {
      const vals = (f.attributes[d.id] ?? []).map((v) => v.trim()).filter(Boolean);
      return { attributeId: d.id, value: d.type === 'multiselect' ? vals : (vals[0] ?? '') };
    })
    .filter((a) => (Array.isArray(a.value) ? a.value.length > 0 : a.value !== ''));
}

function toBody(f: ProductForm, canCost: boolean, defs: AttributeDef[] | undefined) {
  const options = f.options
    .filter((o) => o.name.trim())
    .map((o) => ({
      name: o.name.trim(),
      values: [
        ...new Set(
          o.values
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean),
        ),
      ],
    }));
  return {
    title: f.title.trim(),
    slug: f.slug.trim() || undefined,
    brandId: f.brandId,
    species: f.species,
    shortDescription: optional(f.shortDescription),
    description: optional(f.description),
    composition: optional(f.composition),
    usage: optional(f.usage),
    warnings: optional(f.warnings),
    feedRegistrationNo: optional(f.feedRegistrationNo),
    taxClassId: f.taxClassId || null,
    hsCode: optional(f.hsCode),
    originCountry: f.originCountry || null,
    customsDescription: optional(f.customsDescription),
    b2cVisible: f.b2cVisible,
    b2bVisible: f.b2bVisible,
    dealerMinQuantity: f.dealerMinQuantity.trim() ? Number(f.dealerMinQuantity) : null,
    dealerPackSize: f.dealerPackSize.trim() ? Number(f.dealerPackSize) : null,
    healthContent: f.healthContent,
    subscribable: f.subscribable,
    seoTitle: optional(f.seoTitle),
    seoDescription: optional(f.seoDescription),
    merchant: {
      exclude: !f.merchantInclude,
      googleCategory: optional(f.googleCategory),
      condition: f.condition,
      identifierExists: f.identifierExists,
      skuIsMpn: f.skuIsMpn,
      // Etiketin sırası Google'daki numarasıdır (custom_label_0…4); boş etiket gönderilmez.
      customLabels: f.customLabels.map((l) => l.trim()),
    },
    categoryIds: f.categoryIds,
    tags: f.tags
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean),
    // Tanımlar yüklenmeden kaydedilirse özellik listesi gönderilmez (mevcut değerler korunur).
    ...(defs ? { attributes: attributeBody(f, defs) } : {}),
    options,
    variants: f.variants.map((v) => ({
      ...(v.id ? { id: v.id } : {}),
      sku: v.sku.trim(),
      barcode: optional(v.barcode),
      title: '',
      optionValues: Object.fromEntries(options.map((o) => [o.name, v.optionValues[o.name] ?? ''])),
      weightGrams: v.weightGrams.trim() ? Number(v.weightGrams) : null,
      ...(canCost
        ? { costAmountMinor: toMinor(v.cost, 'TRY'), costCurrency: v.cost.trim() ? 'TRY' : null }
        : {}),
      status: v.status,
    })),
  };
}

export function ProductEditorPage() {
  const t = useT();
  const { id } = useParams({ from: '/app/urunler/$id' });
  const isNew = id === 'yeni';
  const detail = useQuery({
    queryKey: ['product', id],
    queryFn: () => api.get<ProductDetail>(`/v1/admin/products/${id}`),
    enabled: !isNew,
  });
  if (!isNew && detail.isLoading) return <Spinner label={t('common.loading')} />;
  return <ProductEditor key={detail.data?.version ?? 'new'} product={isNew ? null : (detail.data ?? null)} />;
}

function ProductEditor({ product }: { product: ProductDetail | null }) {
  const t = useT();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { brands, categories, taxes } = useCatalogRefs();
  const canWrite = can('catalog.product.write');
  const canCost = can('pricing.cost.read');
  const [f, setF] = useState<ProductForm>(() => (product ? fromDetail(product) : emptyForm()));
  const [extras, setExtras] = useState(() => extrasFromDetail(product));
  const attrDefs = useAttributeDefs();
  const { locale } = useI18n();
  const countries = useQuery({
    queryKey: ['countries'],
    queryFn: () => api.get<Country[]>('/v1/admin/countries'),
    staleTime: 3_600_000,
  });
  useEffect(() => {
    if (!product && !f.brandId && brands[0]) setF((x) => ({ ...x, brandId: brands[0]!.id }));
  }, [brands, product, f.brandId]);
  const save = useApiMutation(
    () => {
      const body = { ...toBody(f, canCost, attrDefs.data), ...extrasBody(extras) };
      return product
        ? api.patch<{ id: string }>(
            `/v1/admin/products/${product.id}`,
            { ...body, status: f.status },
            {
              ifMatch: product.version,
            },
          )
        : api.post<{ id: string }>('/v1/admin/products', body);
    },
    {
      invalidate: [['products'], ['product']],
      success: t('common.saved'),
      silentFieldErrors: true,
      onSuccess: (r) => {
        if (!product) void navigate({ to: '/urunler/$id', params: { id: r.id } });
      },
    },
  );
  const err = (k: string) => fieldError(save.error, k);
  const set = <K extends keyof ProductForm>(k: K, v: ProductForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const optionNames = f.options.map((o) => o.name.trim()).filter(Boolean);

  return (
    <PageFrame
      helpKey="page.product_editor"
      title={product ? product.title : t('admin.products.createTitle')}
      actions={
        <>
          <Link to="/urunler" className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.products.backToList')}
          </Link>
          {product && <ProductToolsActions product={product} />}
          {canWrite && (
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
              {t('common.save')}
            </Button>
          )}
        </>
      }
    >
      {save.error && save.error.errors.length > 0 && (
        <p role="alert" className="rounded-sm border border-rose/30 bg-rose-tint px-3 py-2 text-sm text-rose">
          {save.error.errors.map((e) => `${e.field}: ${e.message}`).join(' · ')}
        </p>
      )}
      {product && <ClaimsAndReview product={product} />}
      {!product && canWrite && (
        <Section
          title={t('admin.productTools.startFrom')}
          description={t('admin.productTools.startFromLead')}
        >
          <TemplatePicker onApply={(patch) => setF((x) => ({ ...x, ...patch }))} />
        </Section>
      )}

      <Section title={t('admin.products.basics')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('admin.products.title')} required error={err('title')} className="sm:col-span-2">
            {(p) => (
              <Input
                {...p}
                value={f.title}
                disabled={!canWrite}
                onChange={(e) =>
                  setF({
                    ...f,
                    title: e.target.value,
                    slug: f.slugTouched ? f.slug : slugify(e.target.value),
                  })
                }
              />
            )}
          </Field>
          <Field label={t('admin.products.slug')} error={err('slug')} help={<HelpTip k="products.slug" />}>
            {(p) => (
              <Input
                {...p}
                value={f.slug}
                disabled={!canWrite}
                onChange={(e) => setF({ ...f, slug: e.target.value, slugTouched: true })}
              />
            )}
          </Field>
          <Field label={t('admin.products.brand')} required error={err('brandId')}>
            {(p) => (
              <Select
                {...p}
                value={f.brandId}
                disabled={!canWrite}
                onChange={(e) => set('brandId', e.target.value)}
              >
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.products.species')} help={<HelpTip k="products.species" />}>
            {() => (
              <div className="flex flex-wrap gap-x-4 gap-y-2 pt-2">
                {SPECIES.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="accent-[var(--zt-pine)]"
                      disabled={!canWrite}
                      checked={f.species.includes(s)}
                      onChange={(e) =>
                        set(
                          'species',
                          e.target.checked ? [...f.species, s] : f.species.filter((x) => x !== s),
                        )
                      }
                    />
                    {t(`admin.products.speciesNames.${s}`)}
                  </label>
                ))}
              </div>
            )}
          </Field>
          <Field label={t('admin.products.taxClass')} help={<HelpTip k="products.tax_class" />}>
            {(p) => (
              <Select
                {...p}
                value={f.taxClassId}
                disabled={!canWrite}
                onChange={(e) => set('taxClassId', e.target.value)}
              >
                <option value="">{t('admin.products.defaultTax')}</option>
                {taxes.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {product && (
            <Field label={t('admin.products.status')} help={<HelpTip k="products.status" />}>
              {(p) => (
                <Select
                  {...p}
                  value={f.status}
                  disabled={!canWrite}
                  onChange={(e) => set('status', e.target.value as ProductForm['status'])}
                >
                  {['draft', 'active', 'archived'].map((s) => (
                    <option key={s} value={s}>
                      {t(`admin.products.statuses.${s}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <div className="space-y-3 sm:col-span-2">
            <ToggleRow
              label={t('admin.products.b2cVisible')}
              checked={f.b2cVisible}
              disabled={!canWrite}
              onChange={(v) => set('b2cVisible', v)}
              help={<HelpTip k="products.visibility" />}
            />
            <ToggleRow
              label={t('admin.products.b2bVisible')}
              checked={f.b2bVisible}
              disabled={!canWrite}
              onChange={(v) => set('b2bVisible', v)}
            />
            {f.b2bVisible && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label={t('admin.products.dealerMinQuantity')}
                  optionalLabel={t('common.optional')}
                  help={<HelpTip k="products.dealer_min_quantity" />}
                >
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="numeric"
                      disabled={!canWrite}
                      value={f.dealerMinQuantity}
                      onChange={(e) => set('dealerMinQuantity', e.target.value.replace(/\D/g, ''))}
                    />
                  )}
                </Field>
                <Field
                  label={t('admin.products.dealerPackSize')}
                  optionalLabel={t('common.optional')}
                  help={<HelpTip k="products.dealer_pack_size" />}
                >
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="numeric"
                      disabled={!canWrite}
                      value={f.dealerPackSize}
                      onChange={(e) => set('dealerPackSize', e.target.value.replace(/\D/g, ''))}
                    />
                  )}
                </Field>
              </div>
            )}
            <ToggleRow
              label={t('admin.products.healthContent')}
              checked={f.healthContent}
              disabled={!canWrite}
              onChange={(v) => set('healthContent', v)}
              help={<HelpTip k="products.health_content" />}
            />
            <ToggleRow
              label={t('admin.products.subscribable')}
              checked={f.subscribable}
              disabled={!canWrite}
              onChange={(v) => set('subscribable', v)}
              help={<HelpTip k="products.subscribable" />}
            />
          </div>
        </div>
      </Section>

      <Section
        title={t('admin.products.content')}
        description={t('admin.products.contentLead')}
        action={
          canWrite && product ? (
            <ProductDescriptionDraft
              productId={product.id}
              onApply={(v) => {
                set('shortDescription', v.shortDescription);
                set('description', v.description);
              }}
            />
          ) : undefined
        }
      >
        <div className="grid gap-4">
          <Field label={t('admin.products.shortDescription')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Textarea
                {...p}
                className="min-h-16"
                value={f.shortDescription}
                disabled={!canWrite}
                onChange={(e) => set('shortDescription', e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.products.description')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Textarea
                {...p}
                className="min-h-40"
                value={f.description}
                disabled={!canWrite}
                onChange={(e) => set('description', e.target.value)}
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('admin.products.composition')} optionalLabel={t('common.optional')}>
              {(p) => (
                <Textarea
                  {...p}
                  value={f.composition}
                  disabled={!canWrite}
                  onChange={(e) => set('composition', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('admin.products.usage')} optionalLabel={t('common.optional')}>
              {(p) => (
                <Textarea
                  {...p}
                  value={f.usage}
                  disabled={!canWrite}
                  onChange={(e) => set('usage', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('admin.products.warnings')} optionalLabel={t('common.optional')}>
              {(p) => (
                <Textarea
                  {...p}
                  value={f.warnings}
                  disabled={!canWrite}
                  onChange={(e) => set('warnings', e.target.value)}
                />
              )}
            </Field>
            <Field
              label={t('admin.products.feedRegistrationNo')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="products.feed_registration_no" />}
            >
              {(p) => (
                <Input
                  {...p}
                  value={f.feedRegistrationNo}
                  disabled={!canWrite}
                  onChange={(e) => set('feedRegistrationNo', e.target.value)}
                />
              )}
            </Field>
          </div>
        </div>
      </Section>

      <Section title={t('admin.products.categories')}>
        {categories.length === 0 ? (
          <p className="text-sm text-ink-3">{t('admin.products.noCategories')}</p>
        ) : (
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {categories.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-[var(--zt-pine)]"
                  disabled={!canWrite}
                  checked={f.categoryIds.includes(c.id)}
                  onChange={(e) =>
                    set(
                      'categoryIds',
                      e.target.checked ? [...f.categoryIds, c.id] : f.categoryIds.filter((x) => x !== c.id),
                    )
                  }
                />
                {c.name}
              </label>
            ))}
          </div>
        )}
      </Section>

      <TagsAndAttributes
        f={f}
        defs={attrDefs.data}
        disabled={!canWrite}
        onChange={(patch) => setF((x) => ({ ...x, ...patch }))}
        error={err}
      />

      <ProductExtrasSection
        value={extras}
        onChange={setExtras}
        disabled={!canWrite}
        error={err}
        productId={product?.id ?? null}
      />

      <Section
        title={t('admin.products.variants')}
        description={t('admin.products.variantsLead')}
        action={<HelpTip k="products.variants" />}
      >
        <div className="space-y-3">
          {f.options.map((o, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[12rem_1fr_auto]">
              <Input
                aria-label={t('admin.products.optionName')}
                placeholder={t('admin.products.optionNamePlaceholder')}
                value={o.name}
                disabled={!canWrite}
                onChange={(e) =>
                  set(
                    'options',
                    f.options.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                  )
                }
              />
              <Input
                aria-label={t('admin.products.optionValues')}
                placeholder={t('admin.products.optionValuesPlaceholder')}
                value={o.values}
                disabled={!canWrite}
                onChange={(e) =>
                  set(
                    'options',
                    f.options.map((x, j) => (j === i ? { ...x, values: e.target.value } : x)),
                  )
                }
              />
              {canWrite && (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t('admin.products.removeOption')}
                  onClick={() =>
                    set(
                      'options',
                      f.options.filter((_, j) => j !== i),
                    )
                  }
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              )}
            </div>
          ))}
          {canWrite && f.options.length < 3 && (
            <Button size="sm" onClick={() => set('options', [...f.options, { name: '', values: '' }])}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.products.addOption')}
            </Button>
          )}
        </div>
        <div className="mt-4 overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                {optionNames.map((n) => (
                  <th key={n} className="px-2 py-2 text-start font-medium">
                    {n}
                  </th>
                ))}
                <th className="px-2 py-2 text-start font-medium">
                  <span className="inline-flex items-center gap-1">
                    {t('admin.products.sku')} <HelpTip k="products.sku" />
                  </span>
                </th>
                <th className="px-2 py-2 text-start font-medium">{t('admin.products.barcode')}</th>
                <th className="px-2 py-2 text-start font-medium">{t('admin.products.weight')}</th>
                {canCost && (
                  <th className="px-2 py-2 text-start font-medium">
                    <span className="inline-flex items-center gap-1">
                      {t('admin.products.cost')} <HelpTip k="products.cost" />
                    </span>
                  </th>
                )}
                <th className="px-2 py-2 text-end font-medium">{t('admin.products.stock')}</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {f.variants.map((v, i) => {
                const upd = (patch: Partial<VariantForm>) =>
                  set(
                    'variants',
                    f.variants.map((x, j) => (j === i ? { ...x, ...patch } : x)),
                  );
                const stock = product?.variants.find((x) => x.id === v.id);
                return (
                  <tr
                    key={v.id ?? `n${i}`}
                    className={
                      v.status === 'archived' ? 'border-t border-line opacity-60' : 'border-t border-line'
                    }
                  >
                    {optionNames.map((n) => {
                      const values =
                        f.options
                          .find((o) => o.name.trim() === n)
                          ?.values.split(',')
                          .map((s) => s.trim())
                          .filter(Boolean) ?? [];
                      return (
                        <td key={n} className="px-2 py-1.5">
                          <Select
                            aria-label={n}
                            className="h-9"
                            value={v.optionValues[n] ?? ''}
                            disabled={!canWrite}
                            onChange={(e) =>
                              upd({ optionValues: { ...v.optionValues, [n]: e.target.value } })
                            }
                          >
                            <option value="">—</option>
                            {values.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </Select>
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5">
                      <Input
                        aria-label={t('admin.products.sku')}
                        className="h-9 tabular"
                        value={v.sku}
                        disabled={!canWrite}
                        onChange={(e) => upd({ sku: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        aria-label={t('admin.products.barcode')}
                        className="h-9 tabular"
                        inputMode="numeric"
                        value={v.barcode}
                        disabled={!canWrite}
                        onChange={(e) => upd({ barcode: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        aria-label={t('admin.products.weight')}
                        className="h-9 w-24 tabular"
                        inputMode="numeric"
                        value={v.weightGrams}
                        disabled={!canWrite}
                        onChange={(e) => upd({ weightGrams: e.target.value })}
                      />
                    </td>
                    {canCost && (
                      <td className="px-2 py-1.5">
                        <Input
                          aria-label={t('admin.products.cost')}
                          className="h-9 w-28 tabular"
                          inputMode="decimal"
                          value={v.cost}
                          disabled={!canWrite}
                          onChange={(e) => upd({ cost: e.target.value })}
                        />
                      </td>
                    )}
                    <td className="px-2 py-1.5 text-end tabular">
                      {stock
                        ? `${stock.onHand}${stock.reserved ? ` (${t('admin.products.reservedShort', { n: stock.reserved })})` : ''}`
                        : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-end">
                      {canWrite && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            v.id
                              ? upd({ status: v.status === 'active' ? 'archived' : 'active' })
                              : set(
                                  'variants',
                                  f.variants.filter((_, j) => j !== i),
                                )
                          }
                        >
                          {v.id
                            ? v.status === 'active'
                              ? t('admin.products.archiveVariant')
                              : t('admin.products.restoreVariant')
                            : t('common.remove')}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {canWrite && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() =>
                set('variants', [
                  ...f.variants,
                  { sku: '', barcode: '', weightGrams: '', cost: '', optionValues: {}, status: 'active' },
                ])
              }
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.products.addVariant')}
            </Button>
            <SkuSuggestButton
              f={f}
              productId={product?.id ?? null}
              onApply={(skus) =>
                set(
                  'variants',
                  f.variants.map((v, i) => (v.sku.trim() || !skus[i] ? v : { ...v, sku: skus[i]! })),
                )
              }
            />
          </div>
        )}
      </Section>

      <Section
        title={t('admin.products.customs.title')}
        description={t('admin.products.customs.lead')}
        action={<HelpTip k="products.customs" />}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('admin.products.customs.hsCode')}
            optionalLabel={t('common.optional')}
            description={t('admin.products.customs.hsCodeHint')}
            help={<HelpTip k="products.hs_code" />}
            error={err('hsCode')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                maxLength={20}
                className="tabular"
                value={f.hsCode}
                disabled={!canWrite}
                onChange={(e) => set('hsCode', e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('admin.products.customs.originCountry')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="products.origin_country" />}
            error={err('originCountry')}
          >
            {(p) => (
              <Select
                {...p}
                value={f.originCountry}
                disabled={!canWrite}
                onChange={(e) => set('originCountry', e.target.value)}
              >
                <option value="">—</option>
                {(countries.data ?? []).map((c) => (
                  <option key={c.code} value={c.code}>
                    {locale === 'en' ? c.nameEn : c.nameTr} ({c.code})
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            className="sm:col-span-2"
            label={t('admin.products.customs.description')}
            optionalLabel={t('common.optional')}
            description={t('admin.products.customs.descriptionHint')}
            help={<HelpTip k="products.customs_description" />}
            error={err('customsDescription')}
          >
            {(p) => (
              <Input
                {...p}
                maxLength={200}
                value={f.customsDescription}
                disabled={!canWrite}
                onChange={(e) => set('customsDescription', e.target.value)}
              />
            )}
          </Field>
        </div>
      </Section>

      <Section title={t('admin.products.seo')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('admin.products.seoTitle')}
            optionalLabel={t('common.optional')}
            description={t('admin.products.seoTitleHint')}
          >
            {(p) => (
              <Input
                {...p}
                maxLength={70}
                value={f.seoTitle}
                disabled={!canWrite}
                onChange={(e) => set('seoTitle', e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('admin.products.seoDescription')}
            optionalLabel={t('common.optional')}
            description={t('admin.products.seoDescriptionHint')}
          >
            {(p) => (
              <Textarea
                {...p}
                maxLength={170}
                className="min-h-16"
                value={f.seoDescription}
                disabled={!canWrite}
                onChange={(e) => set('seoDescription', e.target.value)}
              />
            )}
          </Field>
        </div>
      </Section>

      <Section
        title={t('admin.products.merchant.title')}
        description={t('admin.products.merchant.lead')}
        action={<HelpTip k="products.merchant" />}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <ToggleRow
              label={t('admin.products.merchant.include')}
              checked={f.merchantInclude}
              disabled={!canWrite}
              onChange={(v) => set('merchantInclude', v)}
            />
          </div>
          <Field
            label={t('admin.products.merchant.googleCategory')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="products.google_category" />}
          >
            {(p) => (
              <Input
                {...p}
                maxLength={750}
                value={f.googleCategory}
                placeholder="5082"
                disabled={!canWrite}
                onChange={(e) => set('googleCategory', e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.products.merchant.condition')}>
            {(p) => (
              <Select
                {...p}
                value={f.condition}
                disabled={!canWrite}
                onChange={(e) => set('condition', e.target.value as ProductForm['condition'])}
              >
                {(['new', 'refurbished', 'used'] as const).map((c) => (
                  <option key={c} value={c}>
                    {t(`admin.products.merchant.conditions.${c}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.products.merchant.identifierExists')}
            help={<HelpTip k="products.identifier_exists" />}
          >
            {(p) => (
              <Select
                {...p}
                value={f.identifierExists}
                disabled={!canWrite}
                onChange={(e) => set('identifierExists', e.target.value as ProductForm['identifierExists'])}
              >
                {(['auto', 'no'] as const).map((c) => (
                  <option key={c} value={c}>
                    {t(`admin.products.merchant.identifiers.${c}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="self-end pb-2">
            <ToggleRow
              label={t('admin.products.merchant.skuIsMpn')}
              checked={f.skuIsMpn}
              disabled={!canWrite}
              onChange={(v) => set('skuIsMpn', v)}
              help={<HelpTip k="products.sku_is_mpn" />}
            />
          </div>
          <fieldset className="sm:col-span-2">
            <legend className="mb-2 flex items-center gap-1 text-sm font-medium">
              {t('admin.products.merchant.customLabels')}
              <HelpTip k="products.custom_labels" />
            </legend>
            <div className="grid gap-2 sm:grid-cols-5">
              {f.customLabels.map((l, i) => (
                <Field key={i} label={t('admin.products.merchant.customLabel', { n: i })}>
                  {(p) => (
                    <Input
                      {...p}
                      maxLength={100}
                      value={l}
                      disabled={!canWrite}
                      onChange={(e) =>
                        set(
                          'customLabels',
                          f.customLabels.map((x, j) => (j === i ? e.target.value : x)),
                        )
                      }
                    />
                  )}
                </Field>
              ))}
            </div>
          </fieldset>
        </div>
      </Section>

      {product && can('marketing.ads.read') && (
        <Section
          title={t('admin.gads.productBoxTitle')}
          description={t('admin.gads.productsLead')}
          action={<HelpTip k="gads.product_box" />}
        >
          <ProductGoogleAds productId={product.id} />
        </Section>
      )}

      {product && <MediaSection product={product} />}
      {product && can('pricing.price.read') && <PricesSection product={product} />}
      {product && <ListingsSection product={product} />}
      {product && <ProductComplianceSection productId={product.id} />}
      {product && <ProductTranslationsSection productId={product.id} />}
    </PageFrame>
  );
}

function TagsAndAttributes({
  f,
  defs,
  disabled,
  onChange,
  error,
}: {
  f: ProductForm;
  defs: AttributeDef[] | undefined;
  disabled: boolean;
  onChange: (patch: Partial<ProductForm>) => void;
  error: (field: string) => string | undefined;
}) {
  const t = useT();
  const popular = useQuery({
    queryKey: ['product-tags'],
    queryFn: () => api.get<ProductTag[]>('/v1/admin/product-tags'),
  });
  const current = f.tags
    .split(',')
    .map((x) => x.trim().toLocaleLowerCase('tr'))
    .filter(Boolean);
  const suggestions = (popular.data ?? []).filter((x) => !current.includes(x.tag)).slice(0, 12);
  const sent = defs ? attributeBody(f, defs).map((a) => a.attributeId) : [];
  const setAttr = (id: string, values: string[]) =>
    onChange({ attributes: { ...f.attributes, [id]: values } });
  return (
    <Section
      title={t('admin.products.tagsAttributes')}
      description={t('admin.products.tagsAttributesLead')}
      action={<HelpTip k="products.attributes" />}
    >
      <div className="grid gap-4">
        <Field
          label={t('admin.products.tags')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="products.tags" />}
          error={error('tags')}
        >
          {(p) => (
            <Input
              {...p}
              value={f.tags}
              disabled={disabled}
              placeholder={t('admin.products.tagsPlaceholder')}
              onChange={(e) => onChange({ tags: e.target.value })}
            />
          )}
        </Field>
        {!disabled && suggestions.length > 0 && (
          <div className="-mt-2 flex flex-wrap items-center gap-1 text-sm">
            <span className="text-ink-3">{t('admin.products.tagSuggestions')}</span>
            {suggestions.map((s) => (
              <Button
                key={s.tag}
                size="sm"
                variant="ghost"
                onClick={() => onChange({ tags: [...current, s.tag].join(', ') })}
              >
                + {s.tag}
              </Button>
            ))}
          </div>
        )}
        {!defs ? (
          <Spinner label={t('common.loading')} />
        ) : defs.length === 0 ? (
          <p className="text-sm text-ink-3">
            {t('admin.products.noAttributes')}{' '}
            <Link to="/urun-ozellikleri" className="text-pine underline underline-offset-2">
              {t('admin.nav.attributes')}
            </Link>
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {defs.map((d) => {
              const vals = f.attributes[d.id] ?? [];
              const label = d.unit ? `${d.name} (${d.unit})` : d.name;
              const idx = sent.indexOf(d.id);
              const fieldErr = idx >= 0 ? error(`attributes.${idx}.value`) : undefined;
              if (d.type === 'multiselect')
                return (
                  <fieldset key={d.id} className="grid gap-1">
                    <legend className="mb-1 text-sm font-medium">{label}</legend>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {d.options.map((o) => (
                        <label key={o} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="accent-[var(--zt-pine)]"
                            disabled={disabled}
                            checked={vals.includes(o)}
                            onChange={(e) =>
                              setAttr(d.id, e.target.checked ? [...vals, o] : vals.filter((x) => x !== o))
                            }
                          />
                          {o}
                        </label>
                      ))}
                    </div>
                    {fieldErr && <p className="text-sm text-rose">{fieldErr}</p>}
                  </fieldset>
                );
              return (
                <Field key={d.id} label={label} optionalLabel={t('common.optional')} error={fieldErr}>
                  {(p) =>
                    d.type === 'select' || d.type === 'boolean' ? (
                      <Select
                        {...p}
                        value={vals[0] ?? ''}
                        disabled={disabled}
                        onChange={(e) => setAttr(d.id, e.target.value ? [e.target.value] : [])}
                      >
                        <option value="">{t('admin.products.attributeEmpty')}</option>
                        {(d.type === 'boolean' ? ['true', 'false'] : d.options).map((o) => (
                          <option key={o} value={o}>
                            {o === 'true' ? t('common.yes') : o === 'false' ? t('common.no') : o}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Input
                        {...p}
                        value={vals[0] ?? ''}
                        disabled={disabled}
                        inputMode={d.type === 'number' ? 'decimal' : undefined}
                        maxLength={200}
                        onChange={(e) => setAttr(d.id, [e.target.value])}
                      />
                    )
                  }
                </Field>
              );
            })}
          </div>
        )}
      </div>
    </Section>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
  disabled,
  help,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  help?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} label={label} />
      <span className="text-sm">{label}</span>
      {help}
    </div>
  );
}

function ClaimsAndReview({ product }: { product: ProductDetail }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [note, setNote] = useState('');
  const review = useApiMutation(
    (decision: 'approved' | 'rejected') =>
      api.post(`/v1/admin/products/${product.id}/vet-review`, {
        decision,
        note: note.trim() || null,
        contentHash: product.contentHashCurrent,
      }),
    { invalidate: [['product'], ['products']], success: t('admin.products.reviewSaved') },
  );
  if (!product.healthContent) return null;
  return (
    <Section
      title={t('admin.products.vetReviewTitle')}
      description={t('admin.products.vetReviewLead')}
      action={<HelpTip k="products.vet_review" />}
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={vetTone[product.vetReviewStatus]}>
            {t(`admin.products.vetStatuses.${product.vetReviewStatus}`)}
          </Badge>
          {product.vetReviewedAt && (
            <span className="text-sm text-ink-3">{formatDate(product.vetReviewedAt, true)}</span>
          )}
        </div>
        {product.vetReviewNote && <p className="text-sm text-ink-2">{product.vetReviewNote}</p>}
        {product.claimFindings.length > 0 ? (
          <div className="rounded-sm border border-amber/30 bg-amber-tint p-3">
            <p className="mb-2 text-sm font-medium text-amber">{t('admin.products.claimsFound')}</p>
            <ul className="space-y-1 text-sm">
              {product.claimFindings.map((c, i) => (
                <li key={i}>
                  <strong>{t(`healthClaims.${c.code}`)}</strong> — {t(`admin.products.fields.${c.field}`)}: “
                  {c.excerpt}”
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-ink-3">{t('admin.products.noClaims')}</p>
        )}
        {can('catalog.health_claim.approve') && (
          <div className="space-y-2">
            <Field label={t('admin.products.reviewNote')} optionalLabel={t('admin.products.reviewNoteHint')}>
              {(p) => (
                <Textarea
                  {...p}
                  className="min-h-16"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" loading={review.isPending} onClick={() => review.mutate('approved')}>
                {t('admin.products.approveContent')}
              </Button>
              <Button variant="danger" loading={review.isPending} onClick={() => review.mutate('rejected')}>
                {t('admin.products.rejectContent')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function MediaSection({ product }: { product: ProductDetail }) {
  const t = useT();
  const { can } = usePermissions();
  const canWrite = can('catalog.product.write');
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const add = useApiMutation(
    (fileId: string) => api.post(`/v1/admin/products/${product.id}/media`, { fileId }),
    {
      invalidate: [['product']],
    },
  );
  const remove = useApiMutation(
    (mediaId: string) => api.del(`/v1/admin/products/${product.id}/media/${mediaId}`),
    {
      invalidate: [['product']],
      success: t('common.saved'),
    },
  );
  const setAlt = useApiMutation(
    (v: { mediaId: string; alt: string }) =>
      api.patch(`/v1/admin/products/${product.id}/media/${v.mediaId}`, { alt: v.alt || null }),
    { invalidate: [['product']], success: t('common.saved') },
  );
  const retry = useApiMutation(
    (mediaId: string) => api.post(`/v1/admin/products/${product.id}/media/${mediaId}/retry`),
    { invalidate: [['product']], success: t('admin.products.mediaRetryQueued') },
  );
  const reorder = useApiMutation(
    (mediaIds: string[]) => api.put(`/v1/admin/products/${product.id}/media/order`, { mediaIds }),
    { invalidate: [['product']], success: t('admin.products.mediaReordered') },
  );
  const move = (i: number, dir: -1 | 1) => {
    const ids = product.media.map((m) => m.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };
  return (
    <Section title={t('admin.products.media')} description={t('admin.products.mediaLead')}>
      <ol className="flex flex-wrap gap-4">
        {product.media.map((m, i) => (
          <li key={m.id} className="w-44 space-y-1.5">
            <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-sm border border-line bg-surface-2">
              {m.fileId ? (
                <img src={fileUrl(m.fileId)} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="px-2 text-center text-xs text-ink-3">
                  {t(`admin.products.mediaStatus.${m.status}`)}
                  {m.status === 'failed' && (
                    <span className="mt-0.5 block text-rose">
                      {t(`admin.products.mediaFailure.${m.failureReason ?? 'unknown'}`)}
                      {m.failureDetail ? ` (${m.failureDetail})` : ''}
                    </span>
                  )}
                </span>
              )}
            </div>
            {m.status === 'failed' && m.sourceUrl && canWrite && (
              <span className="flex items-center gap-1">
                <Button
                  size="sm"
                  loading={retry.isPending && retry.variables === m.id}
                  onClick={() => retry.mutate(m.id)}
                >
                  {t('admin.products.mediaRetry')}
                </Button>
                <HelpTip k="products.media_retry" />
              </span>
            )}
            <MediaAltInput
              key={`${m.id}-${m.alt ?? ''}`}
              label={t('admin.products.mediaAlt', { n: i + 1 })}
              value={m.alt ?? ''}
              disabled={!canWrite}
              onSave={(alt) => setAlt.mutate({ mediaId: m.id, alt })}
            />
            {m.width && m.height ? (
              <p className="text-xs text-ink-3">
                {t('admin.products.mediaSize', { width: m.width, height: m.height })}
                {Math.min(m.width, m.height) < 500 && (
                  <span className="mt-0.5 block text-amber">{t('admin.products.mediaSmall')}</span>
                )}
              </p>
            ) : null}
            {canWrite && (
              <div className="flex flex-wrap items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={i === 0 || reorder.isPending}
                  aria-label={t('admin.products.mediaMoveLeft', { n: i + 1 })}
                  onClick={() => move(i, -1)}
                >
                  <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={i === product.media.length - 1 || reorder.isPending}
                  aria-label={t('admin.products.mediaMoveRight', { n: i + 1 })}
                  onClick={() => move(i, 1)}
                >
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </Button>
                <ConfirmButton
                  message={t('admin.products.confirmRemoveMedia')}
                  onConfirm={() => remove.mutateAsync(m.id)}
                >
                  {t('common.remove')}
                </ConfirmButton>
              </div>
            )}
          </li>
        ))}
      </ol>
      {product.media.length > 1 && canWrite && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-2">
          {t('admin.products.mediaOrderLead')}
          <HelpTip k="products.media_order" />
        </p>
      )}
      {canWrite && can('org.file.upload') && (
        <div className="mt-3 flex items-center gap-1">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/avif"
            aria-label={t('admin.products.addImage')}
            tabIndex={-1}
            className="sr-only"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setBusy(true);
              try {
                const fd = new FormData();
                fd.append('file', file);
                const r = await api.upload<{ id: string }>('/v1/admin/files?purpose=general', fd);
                await add.mutateAsync(r.id);
                toast.success(t('admin.products.imageAdded'));
              } catch (err) {
                toast.error(err instanceof ApiError ? err.title : String(err));
              } finally {
                setBusy(false);
                if (input.current) input.current.value = '';
              }
            }}
          />
          <Button size="sm" loading={busy} onClick={() => input.current?.click()}>
            <ImageUp className="h-4 w-4" aria-hidden />
            {t('admin.products.addImage')}
          </Button>
          <HelpTip k="products.media_upload" />
        </div>
      )}
    </Section>
  );
}

/** Alternatif metin: alandan çıkınca (değiştiyse) kaydedilir. */
function MediaAltInput({
  label,
  value,
  disabled,
  onSave,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onSave: (alt: string) => void;
}) {
  const [v, setV] = useState(value);
  return (
    <Field label={label} help={<HelpTip k="products.media_alt" />}>
      {(p) => (
        <Input
          {...p}
          value={v}
          maxLength={200}
          disabled={disabled}
          onChange={(e) => setV(e.target.value)}
          onBlur={() => v.trim() !== value && onSave(v.trim())}
        />
      )}
    </Field>
  );
}

function PricesSection({ product }: { product: ProductDetail }) {
  const t = useT();
  const money = useMoney();
  const { can } = usePermissions();
  const canWrite = can('pricing.price.write');
  const lists = useQuery({
    queryKey: ['price-lists'],
    queryFn: () => api.get<PriceList[]>('/v1/admin/price-lists'),
  });
  const prices = useQuery({
    queryKey: ['product-prices', product.id],
    queryFn: () => api.get<ProductPrices>(`/v1/admin/products/${product.id}/prices`),
  });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const setPrice = useApiMutation(
    (v: { priceListId: string; variantId: string; amountMinor: number | null }) =>
      api.put('/v1/admin/prices', { ...v, minQuantity: 1 }),
    { invalidate: [['product-prices', product.id]], success: t('common.saved') },
  );
  const activeVariants = product.variants.filter((v) => v.status === 'active');
  const current = (listId: string, variantId: string) =>
    prices.data?.find((p) => p.priceListId === listId && p.variantId === variantId && p.minQuantity === 1)
      ?.amountMinor;
  const groups = useMemo(() => lists.data ?? [], [lists.data]);
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/v1/admin/stores') });
  // Vitrin fiyatı mağazanın etkin temel (grupsuz) listesinden gelir; yoksa ürün o mağazada fiyatsız kalır
  // ve listelenmez (F3B-14). Neden: hiç liste yok / etkin değil / para birimi farklı / tarih dışında.
  const missingBase = useMemo(() => {
    if (!lists.data || !stores.data) return [];
    const now = Date.now();
    return stores.data
      .filter((st) => st.status !== 'archived')
      .flatMap((st) => {
        const market = st.markets.find((m) => m.id === st.defaultMarketId) ?? st.markets[0];
        const base = lists.data.filter((l) => l.storeId === st.id && !l.customerGroupId);
        const inDates = (l: PriceList) =>
          (!l.startsAt || Date.parse(l.startsAt) <= now) && (!l.endsAt || Date.parse(l.endsAt) > now);
        const ok = base.some(
          (l) => l.status === 'active' && inDates(l) && (!market || l.currency === market.currency),
        );
        if (ok) return [];
        const reason =
          base.length === 0
            ? 'none'
            : !base.some((l) => l.status === 'active')
              ? 'inactive'
              : market && !base.some((l) => l.currency === market.currency)
                ? 'currency'
                : 'dates';
        return [{ store: st.name, reason, currency: market?.currency ?? '' }];
      });
  }, [lists.data, stores.data]);
  return (
    <Section
      title={t('admin.products.prices')}
      description={t('admin.products.pricesLead')}
      action={<HelpTip k="products.prices" />}
    >
      {missingBase.length > 0 && (
        <div
          className="mb-4 space-y-2 rounded-sm border border-amber/30 bg-amber-tint p-3 text-sm text-ink"
          role="status"
        >
          {missingBase.map((m) => (
            <p key={m.store}>
              {t(`admin.products.noBaseList_${m.reason}`, { store: m.store, currency: m.currency })}
            </p>
          ))}
          {can('pricing.price.read') && (
            <span className="flex items-center gap-1">
              <Link to="/fiyatlar" className={buttonStyles({ size: 'sm' })}>
                {t('admin.products.goPriceLists')}
              </Link>
              <HelpTip k="products.price_list_missing" />
            </span>
          )}
        </div>
      )}
      {groups.length === 0 ? (
        <p className="text-sm text-ink-3">{t('admin.products.noPriceLists')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[36rem] text-sm">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.products.sku')}</th>
                {groups.map((l) => (
                  <th key={l.id} className="px-3 py-2 text-start font-medium">
                    {l.name}
                    <span className="block text-xs font-normal text-ink-3">
                      {l.currency} ·{' '}
                      {l.taxInclusive ? t('admin.pricing.taxIncl') : t('admin.pricing.taxExcl')}
                      {l.status !== 'active' && ` · ${t('admin.products.listInactive')}`}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeVariants.map((v) => (
                <tr key={v.id} className="border-t border-line">
                  <td className="px-3 py-2 tabular">{v.sku}</td>
                  {groups.map((l) => {
                    const k = `${l.id}:${v.id}`;
                    const cur = current(l.id, v.id);
                    return (
                      <td key={l.id} className="px-3 py-1.5">
                        {canWrite ? (
                          <Input
                            aria-label={`${l.name} – ${v.sku}`}
                            className="h-9 w-32 tabular"
                            inputMode="decimal"
                            value={draft[k] ?? minorToInput(cur)}
                            onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                            onBlur={() => {
                              const raw = draft[k];
                              if (raw === undefined) return;
                              const minor = raw.trim() ? toMinor(raw, l.currency) : null;
                              if (raw.trim() && minor === null) {
                                toast.error(t('errors.amount_format'));
                                return;
                              }
                              if (minor === (cur ?? null)) return;
                              setPrice.mutate({ priceListId: l.id, variantId: v.id, amountMinor: minor });
                            }}
                          />
                        ) : (
                          <span className="tabular">{money(cur, l.currency)}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {groups.length > 0 && prices.data && (
        <PriceTiers product={product} lists={groups} prices={prices.data} canWrite={canWrite} />
      )}
    </Section>
  );
}

function ListingsSection({ product }: { product: ProductDetail }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/v1/admin/stores') });
  const [blockers, setBlockers] = useState<string[]>([]);
  const [scheduling, setScheduling] = useState<string | null>(null);
  const publish = useApiMutation(
    (v: { storeId: string; published: boolean }) => api.put(`/v1/admin/products/${product.id}/listing`, v),
    {
      invalidate: [['product']],
      success: t('common.saved'),
      onSuccess: () => setBlockers([]),
    },
  );
  const unschedule = useApiMutation(
    (storeId: string) => api.put(`/v1/admin/products/${product.id}/schedule`, { storeId, publishAt: null }),
    { invalidate: [['product']], success: t('admin.productTools.unscheduled') },
  );
  const canPublish = can('catalog.product.publish');
  return (
    <Section
      title={t('admin.products.listings')}
      description={t('admin.products.listingsLead')}
      action={<HelpTip k="products.publish" />}
    >
      <ul className="divide-y divide-line rounded-md border border-line">
        {stores.data?.map((s) => {
          const listing = product.listings.find((l) => l.storeId === s.id);
          const published = Boolean(listing?.published);
          const scheduledAt = !published ? (listing?.scheduledAt ?? null) : null;
          const failed = listing?.scheduleError ? listing.scheduleError.split(',') : [];
          return (
            <li key={s.id} className="px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="font-medium">{s.name}</span>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={published ? 'moss' : scheduledAt ? 'amber' : 'slate'}>
                    {published
                      ? t('admin.products.published')
                      : scheduledAt
                        ? t('admin.productTools.scheduledFor', { date: formatDate(scheduledAt, true) })
                        : t('admin.products.notPublished')}
                  </Badge>
                  {canPublish && (
                    <Button
                      size="sm"
                      variant={published ? 'secondary' : 'primary'}
                      loading={publish.isPending}
                      onClick={() =>
                        publish.mutate(
                          { storeId: s.id, published: !published },
                          {
                            onError: (e) => {
                              if (e.code === 'cannot_publish')
                                setBlockers(String(e.params.reasons ?? '').split(','));
                            },
                          },
                        )
                      }
                    >
                      {published ? t('admin.products.unpublish') : t('admin.products.publish')}
                    </Button>
                  )}
                  {canPublish && !published && !scheduledAt && (
                    <span className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setScheduling(s.id)}>
                        {t('admin.productTools.schedule')}
                      </Button>
                      <HelpTip k="products.schedule" />
                    </span>
                  )}
                  {canPublish && scheduledAt && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={unschedule.isPending}
                      onClick={() => unschedule.mutate(s.id)}
                    >
                      {t('admin.productTools.unschedule')}
                    </Button>
                  )}
                </div>
              </div>
              {failed.length > 0 && (
                <div
                  role="status"
                  className="mt-2 rounded-sm border border-amber/30 bg-amber-tint p-2 text-sm"
                >
                  <p className="font-medium">{t('admin.productTools.scheduleFailed')}</p>
                  <ul className="list-disc ps-5">
                    {failed.map((b) => (
                      <li key={b}>{t(`publishBlockers.${b}`)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {blockers.length > 0 && (
        <ul
          role="alert"
          className="mt-3 space-y-1 rounded-sm border border-amber/30 bg-amber-tint p-3 text-sm"
        >
          {blockers.map((b) => (
            <li key={b}>{t(`publishBlockers.${b}`)}</li>
          ))}
        </ul>
      )}
      {scheduling && (
        <ScheduleDialog productId={product.id} storeId={scheduling} onClose={() => setScheduling(null)} />
      )}
    </Section>
  );
}

/** Yarın 09:00 (yerel saat), datetime-local biçiminde. */
function tomorrowNine(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
}

function ScheduleDialog({
  productId,
  storeId,
  onClose,
}: {
  productId: string;
  storeId: string;
  onClose: () => void;
}) {
  const t = useT();
  const [value, setValue] = useState(tomorrowNine);
  const save = useApiMutation(
    () =>
      api.put(`/v1/admin/products/${productId}/schedule`, {
        storeId,
        publishAt: value ? new Date(value).toISOString() : null,
      }),
    {
      invalidate: [['product']],
      success: t('admin.productTools.scheduled'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.productTools.schedule')}
      description={t('admin.productTools.scheduleLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('admin.productTools.scheduleSave')}
          </Button>
        </>
      }
    >
      <Field
        label={t('admin.productTools.publishAt')}
        required
        description={t('admin.productTools.publishAtHint')}
        error={
          fieldError(save.error, 'publishAt') ??
          (save.error && !save.error.errors.length ? save.error.title : undefined)
        }
      >
        {(p) => (
          <Input {...p} type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}
